import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays, MapPin } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { localeOf } from "../i18n";

export default function EventsList() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("upcoming");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const locale = localeOf(i18n.language);

  useEffect(() => {
    setLoading(true);
    api.get("/events", { params: { scope: tab } })
      .then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="space-y-4" data-testid="events-list-page">
      <h1 className="font-display text-2xl font-extrabold">{t("nav.events")}</h1>
      <div className="flex gap-2 rounded-full bg-muted p-1">
        {["upcoming", "past"].map((k) => (
          <button key={k} data-testid={`events-tab-${k}`} onClick={() => setTab(k)}
            className={`h-11 flex-1 rounded-full text-sm font-bold transition-colors ${
              tab === k ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
            {t(`events.${k}`)}
          </button>
        ))}
      </div>

      {loading && <p className="py-10 text-center text-sm text-muted-foreground">{t("common.loading")}</p>}
      {!loading && events.length === 0 && (
        <p data-testid="events-empty" className="vy-card p-6 text-center text-sm text-muted-foreground">
          {t("events.noEvents")}
        </p>
      )}
      <ul className="vy-stagger space-y-3">
        {events.map((e) => (
          <li key={e.id}>
            <Link to={`/events/${e.slug || e.id}`} data-testid={`event-card-${e.id}`}
              className="vy-card block p-4 transition-colors hover:border-primary">
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-lg font-bold leading-snug">{e.title}</h2>
                {e.my_participation
                  ? <StatusBadge status={e.my_participation.checked_in_at ? "checked_in" : e.my_participation.status} />
                  : e.status === "cancelled" ? <StatusBadge status="cancelled" /> : null}
              </div>
              <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(e.starts_at, locale)}</p>
                {e.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{e.location}</p>}
              </div>
              <p className="mt-3 text-xs font-semibold text-muted-foreground">
                {e.confirmed_count}/{e.capacity} {t("events.confirmedCount")} · {e.mission_count} {t("events.missions").toLowerCase()}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
